import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import Field from '../../../../../../components/Form/Field'
import EditForm from '../../../../../../features/components/Forms/EditForm'
import SocialAccountsFields from './SocialAccountsFields'
import RefreshableInstantFormField from '../../../AdFacebookCreate/AdFacebookCreateForm/Steps/InstantFormSelectionStep/RefreshableInstantFormField'

import useEditAdPreview from '../../../../../../features/hooks/useEditAdPreview'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  getCustomizedAdLeadGenFormPayload,
  getSingleImageAdLeadGenFormPayload,
  getSingleVideoAdLeadGenFormPayload
} from './leadGenFormHelpers'
import { usePreselectFirstInstagramAccountOnFacebookPageChange } from './hooks'

import { clearUpdateAd, updateAd } from '../../../../../../modules/actions/ads'

import {
  adUpdateErrorSelector,
  adUpdateIsLoadingSelector,
  adWasUpdatedSelector
} from '../../../../../../modules/selectors/ads'
import { lineItemSelector } from '../../../../../../modules/selectors/lineItems'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  FB_PAGE_CHOICE,
  FB_SPONSOR_PAGE_ID,
  INCLUDE_PARTNERSHIP_PAGE,
  INSTA_ACCOUNT_CHOICE,
  INSTAGRAM_ACTOR_ID,
  LEAD_GEN_FORM,
  NAME
} from '../../../fields'
import nameValidation from '../../../../../../features/validations/name'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { SINGLE_MEDIA_ITEM } from '../../../../../ReusableFormFields/AdFileUpload/fields'

const validationSchema = Yup.object(nameValidation)

const NameAndPageForm = ({
  editItemData: ad,
  adValues,
  setSelectedFacebookPage,
  isInternalAd,
  title,
  ...formProps
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItem = useSelector(lineItemSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const successUpdate = useSelector(adWasUpdatedSelector)

  const initialSponsorPageId = adValues[FB_SPONSOR_PAGE_ID]

  const { id, ad_account_id: adAccountId } = ad
  const adAccount = adAccountId || selectedAdAccountId

  const handleSubmit = useCallback(
    values => {
      const sponsorPageId = values[FB_SPONSOR_PAGE_ID]

      // if there is no initial sponsor page, and user has not selected any - don't include sponsor payload
      const includeSponsorPayload = initialSponsorPageId || sponsorPageId

      const { [INSTA_ACCOUNT_CHOICE]: instagramAccountChoice, [FB_PAGE_CHOICE]: facebookPageChoice } = values

      const updateAdData = {
        account: adAccount,
        adset_id: lineItem.id,
        [NAME]: values[NAME],
        creative: {
          ...(includeSponsorPayload && {
            facebook_branded_content: {
              [FB_SPONSOR_PAGE_ID]: values[FB_SPONSOR_PAGE_ID] ? values[FB_SPONSOR_PAGE_ID] : null
            }
          }),

          // page_id and instagram_user_id should be added in 2 places, do not remove any of them
          page_id: facebookPageChoice,
          ...(instagramAccountChoice && { [INSTAGRAM_ACTOR_ID]: instagramAccountChoice }),
          object_story_spec: {
            // page_id and instagram_user_id should be added in 2 places, do not remove any of them
            page_id: facebookPageChoice,
            ...(instagramAccountChoice && { [INSTAGRAM_ACTOR_ID]: instagramAccountChoice }),

            ...(ad.creative.object_story_spec?.link_data?.call_to_action?.value?.[LEAD_GEN_FORM] &&
              getSingleImageAdLeadGenFormPayload(ad, values)),
            ...(ad.creative.object_story_spec?.video_data?.call_to_action?.value?.[LEAD_GEN_FORM] &&
              getSingleVideoAdLeadGenFormPayload(ad, values))
          },
          ...(ad.creative.asset_feed_spec?.call_to_actions?.[0]?.value?.[LEAD_GEN_FORM] &&
            getCustomizedAdLeadGenFormPayload(ad, values))
        },
        ...(isInternalAd && { internal: true }),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }
      dispatch(updateAd(updateAdData, id, FACEBOOK_PLATFORM))
    },
    [lineItem.id, isInternalAd, id, dispatch, title, initialSponsorPageId, adAccount, ad]
  )

  const initialValues = useMemo(
    () => ({
      [NAME]: adValues[NAME] || '',
      [FB_PAGE_CHOICE]: adValues[FB_PAGE_CHOICE] || '',
      [INSTA_ACCOUNT_CHOICE]: adValues[INSTA_ACCOUNT_CHOICE] || '',
      // sponsor pages:
      [INCLUDE_PARTNERSHIP_PAGE]: adValues[FB_SPONSOR_PAGE_ID] || false,
      [FB_SPONSOR_PAGE_ID]: adValues[FB_SPONSOR_PAGE_ID] || '',
      [LEAD_GEN_FORM]: adValues[SINGLE_MEDIA_ITEM][LEAD_GEN_FORM] || ''
    }),
    [adValues]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  useEditAdPreview(purifiedFormik.values)
  const { onUpdateFacebookPageHandler, instagramAccountsWasLoaded } =
    usePreselectFirstInstagramAccountOnFacebookPageChange({ formik })

  useEffect(() => {
    if (adValues[FB_PAGE_CHOICE] !== values[FB_PAGE_CHOICE]) {
      setSelectedFacebookPage(values[FB_PAGE_CHOICE])
    }
  }, [adValues, values, setSelectedFacebookPage])

  const handleClearUpdateAd = useCallback(() => {
    dispatch(clearUpdateAd())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={successUpdate}
      clearEditItem={handleClearUpdateAd}
      errorSelector={adUpdateErrorSelector}
      isLoadingSelector={adUpdateIsLoadingSelector}
      // disable submit button till instagram accounts are loaded, we need to wait for preselection of the first instagram account
      submitBtnDisabled={!instagramAccountsWasLoaded}
      {...formProps}
    >
      <Field formik={purifiedFormik} name={NAME} placeholder={t('Ad Name')} autoComplete="off" enableReinitialize />

      <SocialAccountsFields
        adValues={adValues}
        formik={purifiedFormik}
        onUpdateFacebookPageHandler={onUpdateFacebookPageHandler}
      />

      <RefreshableInstantFormField formik={formik} />
    </EditForm>
  )
}

export default NameAndPageForm
